import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import { useGetInfiniteList } from '@/requests/hooks';
import type { QueryParams } from '@/requests/types';
import type { BaseInstance, ModelName } from '@pigello/pigello-matrix';
import { type ReactNode } from 'react';
import type {
  IntersectionObserverHookRefCallback,
  IntersectionObserverHookRootRefCallback,
} from 'react-intersection-observer-hook';

export default function InfiniteScroll<Instance extends BaseInstance>({
  modelName,
  queryParams,
  nested,
  pageSize,
  children,
  enabled,
  overrideUrl,
}: {
  modelName: ModelName;
  queryParams: QueryParams<Instance>;
  nested?: (keyof Instance)[] | undefined;
  pageSize?: number;
  onReceivedDataCallback?: (data: Instance[]) => void;
  enabled?: boolean;
  overrideUrl?: string;
  children: ({
    rootRef,
    triggerLoadRef,
    data,
    hasNextPage,
    dataLoading,
    isInitialLoading,
  }: {
    rootRef: IntersectionObserverHookRootRefCallback;
    triggerLoadRef: IntersectionObserverHookRefCallback;
    data?: Instance[];
    hasNextPage?: boolean;
    dataLoading: boolean;
    isInitialLoading?: boolean;
  }) => ReactNode;
}) {
  const {
    data,
    isPending,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetInfiniteList({
    modelName,
    overrideUrl,
    queryParams: {
      ...queryParams,
      pageSize: pageSize ?? 25,
    },
    nested,
    enabled,
  });

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage,
    onLoadMore: fetchNextPage,
    disabled: false,
    rootMargin: '0px 0px 200px 0px',
  });

  return (
    <>
      {children({
        data: data?.pages.flatMap((p) => p.list),
        triggerLoadRef: sentryRef,
        rootRef,
        hasNextPage,
        dataLoading: isFetchingNextPage,
        isInitialLoading: isPending,
      })}
    </>
  );
}
